import * as Sentry from '@sentry/react';
import { UserProps } from '@partsbadger/types';
import { REACT_APP_ENVIRONMENT } from '@partsbadger/utils';

import {
    getLuckyOrangeSessionId,
    identifyLuckyOrangeVisitor,
    isLuckyOrangeEnabled,
    LuckyOrangeVisitorMeta,
} from './luckyOrange';

function buildVisitorMeta(user: UserProps, isGuestFlow: boolean): LuckyOrangeVisitorMeta {
    const contact = user.default_contact;

    return {
        email: user.email,
        name: contact?.fullname || user.fullname || `${user.first_name} ${user.last_name}`.trim(),
        contact_id: contact?.id != null ? String(contact.id) : undefined,
        account: contact?.account || undefined,
        is_guest: isGuestFlow,
    };
}

async function syncLuckyOrangeSessionToSentry(): Promise<void> {
    const sessionId = await getLuckyOrangeSessionId();
    if (sessionId) {
        Sentry.setContext('lucky_orange', { session_id: sessionId });
    }
}

function setSentryUserFromIdentity(userId: string, meta: LuckyOrangeVisitorMeta, isGuestFlow: boolean): void {
    Sentry.setUser({
        id: userId,
        email: typeof meta.email === 'string' ? meta.email : undefined,
        username: typeof meta.name === 'string' ? meta.name : undefined,
    });

    if (meta.contact_id) {
        Sentry.setTag('contact_id', meta.contact_id);
    } else {
        Sentry.setTag('contact_id', '');
    }

    if (meta.account) {
        Sentry.setTag('account', meta.account);
    } else {
        Sentry.setTag('account', '');
    }

    Sentry.setContext('portal', {
        environment: REACT_APP_ENVIRONMENT,
        is_guest: isGuestFlow,
    });
}

/**
 * Associates the current portal visitor with Lucky Orange and Sentry.
 * Uses `user.id` as the canonical key for authenticated users.
 */
export function identifyPortalVisitor(user: UserProps | null | undefined, guestEmail?: string | null): void {
    if (!isLuckyOrangeEnabled()) {
        return;
    }

    if (user?.id) {
        const userId = String(user.id);
        const meta = buildVisitorMeta(user, false);

        identifyLuckyOrangeVisitor(userId, meta);
        setSentryUserFromIdentity(userId, meta, false);
        void syncLuckyOrangeSessionToSentry();
        return;
    }

    const email = guestEmail?.trim();
    if (email) {
        const guestId = `guest:${email}`;
        const meta: LuckyOrangeVisitorMeta = {
            email,
            is_guest: true,
        };

        identifyLuckyOrangeVisitor(guestId, meta);
        setSentryUserFromIdentity(guestId, meta, true);
        void syncLuckyOrangeSessionToSentry();
    }
}

/** Clears user identity from Sentry and LO session context (e.g. on logout). */
export function clearPortalVisitorIdentity(): void {
    if (!isLuckyOrangeEnabled()) {
        return;
    }

    Sentry.setUser(null);
    Sentry.setTag('contact_id', '');
    Sentry.setTag('account', '');
    Sentry.setContext('portal', {
        environment: REACT_APP_ENVIRONMENT,
        is_guest: false,
    });
    Sentry.setContext('lucky_orange', {});
}
